import { mergeAttributes, textblockTypeInputRule } from "@tiptap/core";
import DefaultHeading, { type Level } from "@tiptap/extension-heading";
import { calcYchangeDomAttrs, hoverWrapper } from "../Collaboration/YChange";
import { type MarkdownSerializerState } from "prosemirror-markdown";
import { type Node as PMNode } from "@tiptap/pm/model";

// Override the standard heading extension to prevent the input rule from being greedy on returns (\n\r), instead just caring about
// horizontal spacing. Sadly js does not have \h, hence the literal enumeration of it

// We also use # as a shortcut for the space mention command which uses enter to select (this clashed when hitting enter without entering text)

const Heading = DefaultHeading.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      ychange: { default: null },
      id: { default: null },
    };
  },
  addInputRules() {
    return this.options.levels.map((level) => {
      return textblockTypeInputRule({
        find: new RegExp(
          `^(#{1,${level}})[ \f\t\v\u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000\ufeff]$`
        ),
        type: this.type,
        getAttributes: {
          level,
        },
      });
    });
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      `h${node.attrs.level}`,
      mergeAttributes(
        {
          id: HTMLAttributes.id, // id attribute set in LinkHandle plugin
          "heading-type": "heading",
          level: node.attrs.level,
        },
        calcYchangeDomAttrs(node.attrs)
      ),
      ...hoverWrapper(node.attrs.ychange, [0]),
    ];
  },

  addStorage() {
    return {
      markdown: {
        serialize: (state: MarkdownSerializerState, node: PMNode) => {
          if (node.type.name === "title") {
            state.write(`# ${node.textContent}\n\n`);
          } else {
            const level = node.attrs.level;
            const id = node.attrs.id;
            const headingMarks = "#".repeat(level);
            state.write(`${headingMarks} ${node.textContent} {#${id}}\n\n`);

            // final format looks like this:
            // # Title
            //
            // ## Heading 2 {#heading-2-id}
            //
            // ### Heading 3 {#heading-3-id}
          }
        },
      },
    };
  },
});

// New title extension, which is a heading but with a different group used just for documents with a title-first scheme
// prevents it from having styles applied and being copied/pasted elsewhere in the doc with styles.
const Title = Heading.extend({
  name: "title",
  // new group to prevent it from being duplicated/copied/used in rest of doc
  group: "title",
  // only allow ychange mark - we don't want bold/italic headings
  marks: "ychange",

  addOptions() {
    return {
      ...this.parent?.(),
      levels: [1] as Level[],
    };
  },

  parseHTML() {
    return [
      {
        tag: "h1[heading-type=title]",
      },
    ];
  },

  // remove functions that will clash with normal headings - these shouldn't be useable in the title
  addKeyboardShortcuts() {
    return {};
  },
  addCommands() {
    return {};
  },
  addInputRules() {
    return [];
  },
  renderHTML() {
    return [
      "h1",
      {
        "heading-type": "title",
        "no-grab": true, // prevent grab handle and set different top margin
        class: "font-semibold !mt-1",
      },
      0,
    ];
  },
});
export { Heading, Title };
